import { Modal, Tag } from 'antd';
import { History, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { AppConfigVersionMeta } from '@/types';

interface Props {
  currentVersion: AppConfigVersionMeta | null;
  hostedVersions: AppConfigVersionMeta[];
  deployments: AppConfigVersionMeta[];
  canRollback: boolean;
  rollingBack: boolean;
  onRollback: (versionNumber: number) => Promise<boolean>;
}

function stateColor(state?: string) {
  const normalized = (state || '').toUpperCase();
  if (normalized === 'COMPLETE') return 'green';
  if (normalized === 'ROLLING_BACK' || normalized === 'REVERTED')
    return 'orange';
  if (normalized === 'BAKING' || normalized === 'DEPLOYING') return 'blue';
  if (normalized) return 'red';
  return undefined;
}

export function AppConfigVersionPanel({
  currentVersion,
  hostedVersions,
  deployments,
  canRollback,
  rollingBack,
  onRollback,
}: Props) {
  const currentNumber = currentVersion?.version_number || 0;
  const latestHostedNumber = hostedVersions.reduce(
    (latest, version) => Math.max(latest, version.version_number || 0),
    0,
  );
  const requestRollback = (versionNumber: number) => {
    Modal.confirm({
      title: `回滚到 Hosted Version ${versionNumber}？`,
      content:
        '系统会重新执行三文档联合校验，并把历史内容发布成一个新的 Hosted Version；历史版本不会被删除。',
      okText: '确认回滚',
      onOk: async () => {
        const ok = await onRollback(versionNumber);
        if (!ok) throw new Error('回滚失败');
      },
    });
  };

  return (
    <div className="rounded border bg-slate-50 p-4">
      <div className="mb-3 flex items-center gap-2 font-medium">
        <History className="h-4 w-4" /> AppConfig 版本与部署
      </div>
      <div className="mb-3 text-sm">
        当前运行版本：{currentNumber || '-'}{' '}
        {currentVersion?.version_label
          ? `(${currentVersion.version_label})`
          : ''}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            最近 Hosted Versions
          </div>
          <div className="max-h-48 space-y-2 overflow-auto">
            {hostedVersions.length === 0 ? (
              <div className="text-xs text-muted-foreground">暂无版本记录</div>
            ) : (
              hostedVersions.map((version) => (
                <div
                  key={`hosted-${version.version_number}`}
                  className="flex items-center justify-between rounded bg-white p-2 text-xs"
                >
                  <span>
                    v{version.version_number} ·{' '}
                    {version.version_label || '无标签'}
                  </span>
                  {canRollback &&
                  version.version_number !== latestHostedNumber ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={rollingBack}
                      onClick={() => requestRollback(version.version_number)}
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" /> 回滚
                    </Button>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">
            最近 Deployments
          </div>
          <div className="max-h-48 space-y-2 overflow-auto">
            {deployments.length === 0 ? (
              <div className="text-xs text-muted-foreground">暂无部署记录</div>
            ) : (
              deployments.map((deployment, index) => (
                <div
                  key={`deployment-${deployment.deployment_number}-${index}`}
                  className="flex items-center justify-between rounded bg-white p-2 text-xs"
                >
                  <span>
                    #{deployment.deployment_number || '-'} · v
                    {deployment.version_number || '-'}
                  </span>
                  <Tag color={stateColor(deployment.deployment_state)}>
                    {deployment.deployment_state || 'UNKNOWN'}
                  </Tag>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
